import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Map, { Marker, Popup } from 'react-map-gl/mapbox';
import Header from '../../components/Header';
import Footer from '../../components/Footer';
import PageMeta from '../../components/PageMeta';
import api from '../../utils/api';
import { saveUserLocation, getUserLocation, reverseGeocode } from '../../utils/locationUtils';
import { formatFarmerLocation } from '../../utils/addressUtils';
import { getImageUrl, getProfileImageUrl } from '../../utils/imageUtils';
import { useTranslation } from 'react-i18next';
import {
  FaMapMarkerAlt, FaCrosshairs, FaPlus, FaMinus, FaLeaf,
  FaStar, FaPhone, FaEnvelope, FaStore, FaUser,
  FaArrowRight, FaSearch, FaBoxOpen
} from 'react-icons/fa';
import RoleIllustration from '../../components/RoleIllustration';
import { ROLE_IMG } from '../../utils/roleAssets';
import Pagination from '../../components/Pagination';
import 'mapbox-gl/dist/mapbox-gl.css';
import './Nearby.css';

const NB_PER_PAGE = 10;

const MAPBOX_TOKEN = process.env.REACT_APP_MAPBOX_ACCESS_TOKEN;

const DEFAULT_LOCATION = { latitude: 26.4837, longitude: 87.2838 };
const RECENTER_ZOOM = 15;

const Nearby = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const mapRef = useRef(null);
  const [user, setUser] = useState(null);
  const [products, setProducts] = useState([]);
  const [farmers, setFarmers] = useState([]);
  const [consumers, setConsumers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [viewMode, setViewMode] = useState('farmers');
  const [nbPage, setNbPage] = useState(1);

  const [viewport, setViewport] = useState({
    latitude: DEFAULT_LOCATION.latitude,
    longitude: DEFAULT_LOCATION.longitude,
    zoom: 12
  });
  const [userLocation, setUserLocation] = useState(null);
  const [mapCenter, setMapCenter] = useState(DEFAULT_LOCATION);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [selectedFarmer, setSelectedFarmer] = useState(null);
  const [, setLocationStatus] = useState('requesting');
  const [currentAddress, setCurrentAddress] = useState('');

  const watchPositionIdRef = useRef(null);
  const updateIntervalRef = useRef(null);

  useEffect(() => {
    const userData = localStorage.getItem('user');
    if (userData) {
      try {
        const parsedUser = JSON.parse(userData);
        setUser(parsedUser);
        if (parsedUser?.role === 'farmer') {
          setViewMode('consumers');
          startFarmerLocationTracking();
        } else {
          setViewMode('farmers');
        }
      } catch (e) { setViewMode('farmers'); }
    } else { setViewMode('farmers'); }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate]);

  useEffect(() => { setNbPage(1); }, [viewMode]);

  useEffect(() => {
    const savedLocation = getUserLocation();
    if (savedLocation) {
      setUserLocation(savedLocation);
      setMapCenter(savedLocation);
      setViewport({ latitude: savedLocation.latitude, longitude: savedLocation.longitude, zoom: 12 });
      setLocationStatus('success');
    } else {
      getCurrentLocation();
    }

    updateIntervalRef.current = setInterval(() => {
      if (mapCenter.latitude && mapCenter.longitude) {
        const cu = JSON.parse(localStorage.getItem('user') || 'null');
        if (cu?.role === 'farmer') fetchNearbyConsumers();
        else fetchNearbyFarmers();
      }
    }, 5000);

    return () => {
      if (watchPositionIdRef.current !== null) navigator.geolocation.clearWatch(watchPositionIdRef.current);
      if (updateIntervalRef.current) clearInterval(updateIntervalRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (mapCenter.latitude && mapCenter.longitude) {
      if (user?.role === 'farmer') {
        fetchNearbyConsumers();
      } else {
        fetchNearbyFarmers();
        fetchNearbyProducts();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mapCenter, viewMode, user]);

  useEffect(() => {
    if (!userLocation?.latitude || !userLocation?.longitude) return;
    let cancelled = false;
    reverseGeocode(userLocation.latitude, userLocation.longitude)
      .then(addr => {
        if (cancelled) return;
        const parts = [addr.city, addr.district, addr.province].filter(Boolean);
        setCurrentAddress(parts.length > 0 ? parts.join(', ') : addr.displayName?.split(',').slice(0, 3).join(', ') || '');
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [userLocation]);

  const getCurrentLocation = () => {
    setLocationStatus('requesting');
    if (!navigator.geolocation) { fallbackLocation(); return; }
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const loc = { latitude: pos.coords.latitude, longitude: pos.coords.longitude };
        setLocationStatus('success');
        setUserLocation(loc);
        setMapCenter(loc);
        setViewport({ ...loc, zoom: 12 });
        saveUserLocation(loc);
        setTimeout(() => {
          const cu = JSON.parse(localStorage.getItem('user') || 'null');
          if (cu?.role === 'farmer') fetchNearbyConsumers();
          else fetchNearbyFarmers();
        }, 100);
      },
      () => fallbackLocation(),
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 }
    );
  };

  const fallbackLocation = () => {
    setLocationStatus('default');
    setUserLocation(DEFAULT_LOCATION);
    setMapCenter(DEFAULT_LOCATION);
    setViewport({ ...DEFAULT_LOCATION, zoom: 12 });
    setTimeout(() => {
      const cu = JSON.parse(localStorage.getItem('user') || 'null');
      if (cu?.role === 'farmer') fetchNearbyConsumers();
      else fetchNearbyFarmers();
    }, 100);
  };

  const fetchNearbyProducts = async () => {
    setLoading(true);
    try {
      if (!mapCenter.latitude || !mapCenter.longitude) { setLoading(false); return; }
      const params = new URLSearchParams({ lat: mapCenter.latitude.toString(), lng: mapCenter.longitude.toString(), radius: '50' });
      const res = await api.get(`/farmers/products/nearby?${params}`);
      setProducts(res.data || []);
    } catch (e) { setProducts([]); }
    finally { setLoading(false); }
  };

  const fetchNearbyFarmers = async () => {
    try {
      if (!mapCenter.latitude || !mapCenter.longitude) return;
      const params = new URLSearchParams({ lat: mapCenter.latitude.toString(), lng: mapCenter.longitude.toString(), radius: '50' });
      const res = await api.get(`/farmers/nearby?${params}`);
      const valid = (res.data || []).filter(f => {
        if (!f.location?.coordinates) return false;
        const [lng, lat] = f.location.coordinates;
        return !isNaN(lat) && !isNaN(lng) && lat >= 26.0 && lat <= 30.5 && lng >= 80.0 && lng <= 88.5 && f.verified === true;
      });
      setFarmers(valid);
    } catch (e) { setFarmers([]); }
  };

  const fetchNearbyConsumers = async () => {
    try {
      if (!mapCenter.latitude || !mapCenter.longitude) return;
      const params = new URLSearchParams({ lat: mapCenter.latitude.toString(), lng: mapCenter.longitude.toString(), radius: '50' });
      const res = await api.get(`/consumers/nearby?${params}`);
      const valid = (res.data || []).filter(c => {
        if (!c.location?.coordinates) return false;
        const [lng, lat] = c.location.coordinates;
        return !isNaN(lat) && !isNaN(lng) && lat >= 26.0 && lat <= 30.5 && lng >= 80.0 && lng <= 88.5;
      });
      setConsumers(valid);
    } catch (e) { setConsumers([]); }
  };

  const startFarmerLocationTracking = () => {
    if (!navigator.geolocation) return;
    const defaultCoords = [87.2838, 26.4837];
    watchPositionIdRef.current = navigator.geolocation.watchPosition(
      async (pos) => {
        try { await api.put('/farmer/update-location', { coordinates: [pos.coords.longitude, pos.coords.latitude] }); }
        catch (e) { try { await api.post('/farmer/update-location', { coordinates: [pos.coords.longitude, pos.coords.latitude] }); } catch (e2) { /* ignore */ } }
      },
      (err) => {
        if (err.code === err.PERMISSION_DENIED) {
          api.put('/farmer/update-location', { coordinates: defaultCoords }).catch(() =>
            api.post('/farmer/update-location', { coordinates: defaultCoords }).catch(() => {})
          );
        }
      },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 }
    );
  };

  const handleMapMoveEnd = useCallback((evt) => {
    if (!evt?.viewState) return;
    const { latitude, longitude, zoom } = evt.viewState;
    if (latitude && longitude && !isNaN(latitude) && !isNaN(longitude)) {
      setViewport((prev) => {
        const nextZoom = zoom || 12;
        const hasChanged =
          Math.abs((prev.latitude || 0) - latitude) > 0.000001 ||
          Math.abs((prev.longitude || 0) - longitude) > 0.000001 ||
          Math.abs((prev.zoom || 0) - nextZoom) > 0.000001;
        return hasChanged ? { latitude, longitude, zoom: nextZoom } : prev;
      });
      setMapCenter((prev) => {
        const hasChanged =
          Math.abs((prev.latitude || 0) - latitude) > 0.000001 ||
          Math.abs((prev.longitude || 0) - longitude) > 0.000001;
        return hasChanged ? { latitude, longitude } : prev;
      });
    }
  }, []);

  const handleMapClick = useCallback(() => {
    // Keep user's marker tied to real/current location only.
    // Map clicks should not re-assign user location.
  }, []);

  const recenterMap = () => {
    const focusOnLocation = (loc) => {
      const nextView = { latitude: loc.latitude, longitude: loc.longitude, zoom: RECENTER_ZOOM };
      setMapCenter(loc);
      setViewport(nextView);
    };

    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          const loc = { latitude: pos.coords.latitude, longitude: pos.coords.longitude };
          setUserLocation(loc);
          focusOnLocation(loc);
          saveUserLocation(loc);
        },
        () => {
          if (userLocation?.latitude) {
            focusOnLocation(userLocation);
          }
        },
        { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 }
      );
    } else if (userLocation?.latitude) {
      focusOnLocation(userLocation);
    }
  };

  const handleMarkerClick = (item) => {
    if (item.farmer) {
      setSelectedFarmer(item.farmer);
      setSelectedProduct(null);
      if (item.farmer.location?.coordinates) {
        const [lng, lat] = item.farmer.location.coordinates;
        if (typeof lat === 'number') setViewport(prev => ({ ...prev, latitude: lat, longitude: lng, zoom: 14 }));
      }
    } else {
      setSelectedProduct(item);
      setSelectedFarmer(null);
      if (item.farmer?.location?.coordinates) {
        const [lng, lat] = item.farmer.location.coordinates;
        if (typeof lat === 'number') setViewport(prev => ({ ...prev, latitude: lat, longitude: lng, zoom: 14 }));
      }
    }
  };

  const handleListClick = (product) => {
    handleMarkerClick(product);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const activeList = viewMode === 'products' ? products : viewMode === 'consumers' ? consumers : farmers;
  const nbTotalPages = Math.ceil(activeList.length / NB_PER_PAGE);
  const pageProducts = products.slice((nbPage - 1) * NB_PER_PAGE, nbPage * NB_PER_PAGE);
  const pageFarmers = farmers.slice((nbPage - 1) * NB_PER_PAGE, nbPage * NB_PER_PAGE);
  const pageConsumers = consumers.slice((nbPage - 1) * NB_PER_PAGE, nbPage * NB_PER_PAGE);

  return (
    <div className="nb-page">
      <PageMeta
        title={t('nearby.pageTitle')}
        description={t('nearby.pageDescription')}
        path="/nearby"
      />
      <Header />

      <div className="nb-layout">
        {/* Map */}
        <div className="nb-map">
          {MAPBOX_TOKEN && viewport.latitude && viewport.longitude && (
            <Map
              ref={mapRef}
              viewState={{ latitude: viewport.latitude, longitude: viewport.longitude, zoom: viewport.zoom || 12 }}
              onMoveEnd={handleMapMoveEnd}
              mapboxAccessToken={MAPBOX_TOKEN}
              style={{ width: '100%', height: '100%' }}
              mapStyle="mapbox://styles/mapbox/streets-v12"
              onClick={handleMapClick}
              cursor="pointer"
              interactive={true}
            >
              {userLocation && (
                <Marker latitude={userLocation.latitude} longitude={userLocation.longitude} anchor="center">
                  <div className="nb-user-pin">
                    <div className="nb-user-pulse" />
                    <div className="nb-user-dot" />
                  </div>
                </Marker>
              )}

              {user?.role !== 'farmer' && viewMode === 'products' && products.map(p => {
                if (!p.farmer?.location?.coordinates) return null;
                const [lng, lat] = p.farmer.location.coordinates;
                return (
                  <Marker key={p._id} latitude={lat} longitude={lng} anchor="bottom">
                    <div className={`nb-price-pin ${selectedProduct?._id === p._id ? 'nb-pin--active' : ''}`}
                      onClick={e => { e.stopPropagation(); handleMarkerClick(p); }}>
                      Rs. {p.price}
                    </div>
                  </Marker>
                );
              })}

              {user?.role !== 'farmer' && viewMode === 'farmers' && farmers.map(f => {
                if (!f.location?.coordinates) return null;
                const [lng, lat] = f.location.coordinates;
                return (
                  <Marker key={f._id || f.username} latitude={lat} longitude={lng} anchor="bottom">
                    <div className={`nb-avatar-pin ${selectedFarmer?._id === f._id ? 'nb-pin--active' : ''}`}
                      onClick={e => { e.stopPropagation(); handleMarkerClick({ farmer: f, _id: f._id }); }}>
                      {f.profileImage ? (
                        <img src={getProfileImageUrl(f.profileImage)} alt={f.name} referrerPolicy="no-referrer"
                          onError={e => { e.target.onerror = null; e.target.style.display = 'none'; e.target.parentElement.querySelector('.nb-pin-fallback').style.display = 'flex'; }} />
                      ) : null}
                      <span className="nb-pin-fallback" style={{ display: f.profileImage ? 'none' : 'flex' }}>
                        <img src={ROLE_IMG.farmer()} alt="" className="nb-pin-role-img" />
                      </span>
                    </div>
                  </Marker>
                );
              })}

              {user?.role === 'farmer' && viewMode === 'consumers' && consumers.map(c => {
                if (!c.location?.coordinates) return null;
                const [lng, lat] = c.location.coordinates;
                return (
                  <Marker key={c._id || c.username} latitude={lat} longitude={lng} anchor="bottom">
                    <div className={`nb-avatar-pin nb-avatar-pin--consumer ${selectedFarmer?._id === c._id ? 'nb-pin--active' : ''}`}
                      onClick={e => { e.stopPropagation(); handleMarkerClick({ farmer: c, _id: c._id }); }}>
                      {c.profileImage ? (
                        <img src={getProfileImageUrl(c.profileImage)} alt={c.name} referrerPolicy="no-referrer"
                          onError={e => { e.target.onerror = null; e.target.style.display = 'none'; e.target.parentElement.querySelector('.nb-pin-fallback').style.display = 'flex'; }} />
                      ) : null}
                      <span className="nb-pin-fallback" style={{ display: c.profileImage ? 'none' : 'flex' }}><FaUser /></span>
                    </div>
                  </Marker>
                );
              })}

              {selectedProduct && selectedProduct.farmer?.location?.coordinates && (
                <Popup latitude={selectedProduct.farmer.location.coordinates[1]} longitude={selectedProduct.farmer.location.coordinates[0]}
                  onClose={() => setSelectedProduct(null)} closeButton={true} anchor="bottom">
                  <div className="nb-popup">
                    <h4>{selectedProduct.name}</h4>
                    <p className="nb-popup-price">Rs. {selectedProduct.price} / {selectedProduct.unit}</p>
                    <p className="nb-popup-farmer">
                      <img src={ROLE_IMG.farmer()} alt="" className="nb-popup-role-fig" />
                      {selectedProduct.farmer?.name || 'Farmer'}
                    </p>
                    <p className="nb-popup-dist"><FaMapMarkerAlt /> {t('nearby.kmAway', { distance: selectedProduct.distance })}</p>
                    <Link to={`/products/${selectedProduct._id}`} className="nb-popup-btn">{t('nearby.viewDetails')} <FaArrowRight /></Link>
                  </div>
                </Popup>
              )}

              {selectedFarmer && selectedFarmer.location?.coordinates && (
                <Popup latitude={selectedFarmer.location.coordinates[1]} longitude={selectedFarmer.location.coordinates[0]}
                  onClose={() => setSelectedFarmer(null)} closeButton={true} anchor="bottom">
                  <div className="nb-popup">
                    <h4>{selectedFarmer.name}</h4>
                    {selectedFarmer.farmName && <p className="nb-popup-farm"><FaStore /> {selectedFarmer.farmName}</p>}
                    {selectedFarmer.address && (
                      <p className="nb-popup-loc"><FaMapMarkerAlt /> {selectedFarmer.address.district || ''}{selectedFarmer.address.province ? `, ${selectedFarmer.address.province}` : ''}</p>
                    )}
                    {selectedFarmer.phone && <p className="nb-popup-phone"><FaPhone /> {selectedFarmer.phone}</p>}
                    {selectedFarmer.bio && <p className="nb-popup-bio">{selectedFarmer.bio}</p>}
                    {user?.role === 'farmer' ? (
                      <p className="nb-popup-role">
                        <RoleIllustration role="consumer" size="sm" />
                        Consumer
                      </p>
                    ) : (
                      <Link to={`/users/${selectedFarmer.username || selectedFarmer._id}`} className="nb-popup-btn">{t('nearby.viewProfile')} <FaArrowRight /></Link>
                    )}
                  </div>
                </Popup>
              )}
            </Map>
          )}

          <div className="nb-map-controls">
            <button className="nb-ctrl-btn" onClick={recenterMap} title={t('nearby.recenterMap')}><FaCrosshairs /></button>
            <div className="nb-zoom">
              <button onClick={() => setViewport(p => ({ ...p, zoom: Math.min((p.zoom || 12) + 1, 20) }))}><FaPlus /></button>
              <button onClick={() => setViewport(p => ({ ...p, zoom: Math.max((p.zoom || 12) - 1, 1) }))}><FaMinus /></button>
            </div>
          </div>

          <div className="nb-hint">
            <FaMapMarkerAlt /> Drag/zoom the map to explore nearby area.
          </div>
        </div>

        {/* Panel */}
        <div className="nb-panel">
          {/* Current Location */}
          {currentAddress && (
            <div className="nb-current-loc">
              <FaMapMarkerAlt className="nb-current-loc-icon" />
              <span>{t('nearby.youAreIn')} <strong>{currentAddress}</strong></span>
            </div>
          )}

          {/* Toggle */}
          {user?.role !== 'farmer' ? (
            <div className="nb-toggle">
              <button className={`nb-tab ${viewMode === 'farmers' ? 'nb-tab--on' : ''}`} onClick={() => setViewMode('farmers')}>
                <img src={ROLE_IMG.farmer()} alt="" className="nb-section-title-icon" /> {t('nearby.farmers', { count: farmers.length })}
              </button>
              <button className={`nb-tab ${viewMode === 'products' ? 'nb-tab--on' : ''}`} onClick={() => setViewMode('products')}>
                <FaBoxOpen /> {t('nearby.products', { count: products.length })}
              </button>
            </div>
          ) : (
            <div className="nb-toggle">
              <button className="nb-tab nb-tab--on" disabled>
                <FaUser /> {t('nearby.consumers', { count: consumers.length })}
              </button>
            </div>
          )}

          {/* Products */}
          {user?.role !== 'farmer' && viewMode === 'products' && (
            <div className="nb-list">
              {loading ? (
                <div className="nb-empty">
                  <div className="nb-dots"><span /><span /><span /></div>
                  <p>{t('nearby.findingProducts')}</p>
                </div>
              ) : products.length === 0 ? (
                <div className="nb-empty">
                  <FaSearch className="nb-empty-icon" />
                  <h3>{t('nearby.noProductsFound')}</h3>
                  <p>{t('nearby.noProductsAvailable')}</p>
                </div>
              ) : (
                <>
                  {pageProducts.map(product => (
                    <div key={product._id}
                      className={`nb-card ${selectedProduct?._id === product._id ? 'nb-card--active' : ''}`}
                      onClick={() => handleListClick(product)}>
                      <div className="nb-card-img">
                        <img src={getImageUrl(product.images?.[0] || product.image)} alt={product.name}
                          onError={e => { e.target.onerror = null; e.target.src = '/images/deafultpic.png'; }} />
                      </div>
                      <div className="nb-card-body">
                        <h3>{product.name}</h3>
                        <span className="nb-card-price">Rs. {product.price} / {product.unit}</span>
                        <span className="nb-card-farmer">
                          <img src={ROLE_IMG.farmer()} alt="" className="nb-card-farmer-icon" />
                          {product.farmer?.name || 'Farmer'}
                        </span>
                        <div className="nb-card-meta">
                          <span className="nb-card-dist"><FaMapMarkerAlt /> {t('nearby.kmAway', { distance: product.distance })}</span>
                          {product.isOrganic && <span className="nb-card-organic"><FaLeaf /> Organic</span>}
                        </div>
                      </div>
                    </div>
                  ))}
                  <Pagination page={nbPage} totalPages={nbTotalPages} onPageChange={setNbPage} />
                </>
              )}
            </div>
          )}

          {/* Farmers */}
          {user?.role !== 'farmer' && viewMode === 'farmers' && (
            <div className="nb-list">
              {loading ? (
                <div className="nb-empty">
                  <div className="nb-dots"><span /><span /><span /></div>
                  <p>{t('nearby.findingFarmers')}</p>
                </div>
              ) : farmers.length === 0 ? (
                <div className="nb-empty">
                  <img src={ROLE_IMG.farmer()} alt="" className="nb-empty-icon nb-empty-icon--img" />
                  <h3>{t('nearby.noFarmersFound')}</h3>
                  <p>{t('nearby.noFarmersAvailable')}</p>
                </div>
              ) : (
                <>
                  {pageFarmers.map(farmer => (
                    <div key={farmer._id}
                      className={`nb-farmer-card ${selectedFarmer?._id === farmer._id ? 'nb-card--active' : ''}`}
                      onClick={() => {
                        setSelectedFarmer(farmer);
                        if (farmer.location?.coordinates) {
                          const [lng, lat] = farmer.location.coordinates;
                          setViewport(prev => ({ ...prev, latitude: lat, longitude: lng, zoom: 14 }));
                        }
                      }}>
                      <div className="nb-farmer-avatar">
                        {farmer.profileImage ? (
                          <img src={getProfileImageUrl(farmer.profileImage)} alt={farmer.name} referrerPolicy="no-referrer"
                            onError={e => { e.target.onerror = null; e.target.style.display = 'none'; e.target.parentElement.querySelector('.nb-avatar-fallback').style.display = 'flex'; }} />
                        ) : null}
                        <span className="nb-avatar-fallback" style={{ display: farmer.profileImage ? 'none' : 'flex' }}>
                          <img src={ROLE_IMG.farmer()} alt="" className="nb-pin-role-img" />
                        </span>
                      </div>
                      <div className="nb-farmer-body">
                        <h3>{farmer.name}</h3>
                        {farmer.rating > 0 && <span className="nb-farmer-rating"><FaStar /> {farmer.rating} / 5.0</span>}
                        <span className="nb-farmer-loc"><FaMapMarkerAlt /> {formatFarmerLocation(farmer)}</span>
                        {farmer.bio && <p className="nb-farmer-bio">{farmer.bio}</p>}
                        <span className="nb-farmer-count"><FaBoxOpen /> {t('nearby.productsAvailable', { count: farmer.productsCount || 0 })}</span>
                        <Link to={`/users/${farmer.username || farmer._id}`} className="nb-farmer-btn" onClick={e => e.stopPropagation()}>
                          {t('nearby.viewShop')} <FaArrowRight />
                        </Link>
                      </div>
                    </div>
                  ))}
                  <Pagination page={nbPage} totalPages={nbTotalPages} onPageChange={setNbPage} />
                </>
              )}
            </div>
          )}

          {/* Consumers (farmer view) */}
          {user?.role === 'farmer' && viewMode === 'consumers' && (
            <div className="nb-list">
              {loading ? (
                <div className="nb-empty">
                  <div className="nb-dots"><span /><span /><span /></div>
                  <p>{t('nearby.findingConsumers')}</p>
                </div>
              ) : consumers.length === 0 ? (
                <div className="nb-empty">
                  <FaUser className="nb-empty-icon" />
                  <h3>{t('nearby.noConsumersFound')}</h3>
                  <p>{t('nearby.noConsumersAvailable')}</p>
                </div>
              ) : (
                <>
                  {pageConsumers.map(consumer => (
                    <div key={consumer._id}
                      className={`nb-farmer-card ${selectedFarmer?._id === consumer._id ? 'nb-card--active' : ''}`}
                      onClick={() => {
                        setSelectedFarmer(consumer);
                        if (consumer.location?.coordinates) {
                          const [lng, lat] = consumer.location.coordinates;
                          setViewport(prev => ({ ...prev, latitude: lat, longitude: lng, zoom: 14 }));
                        }
                      }}>
                      <div className="nb-farmer-avatar">
                        {consumer.profileImage ? (
                          <img src={getProfileImageUrl(consumer.profileImage)} alt={consumer.name} referrerPolicy="no-referrer"
                            onError={e => { e.target.onerror = null; e.target.style.display = 'none'; e.target.parentElement.querySelector('.nb-avatar-fallback').style.display = 'flex'; }} />
                        ) : null}
                        <span className="nb-avatar-fallback" style={{ display: consumer.profileImage ? 'none' : 'flex' }}><FaUser /></span>
                      </div>
                      <div className="nb-farmer-body">
                        <h3>{consumer.name}</h3>
                        <span className="nb-farmer-loc">
                          <FaMapMarkerAlt /> {consumer.address?.district || consumer.district || 'N/A'}{consumer.address?.province ? `, ${consumer.address.province}` : ''}
                          {consumer.distance && <span> &bull; {t('nearby.kmAway', { distance: consumer.distance })}</span>}
                        </span>
                        {consumer.phone && <span className="nb-consumer-detail"><FaPhone /> {consumer.phone}</span>}
                        {consumer.email && <span className="nb-consumer-detail"><FaEnvelope /> {consumer.email}</span>}
                      </div>
                    </div>
                  ))}
                  <Pagination page={nbPage} totalPages={nbTotalPages} onPageChange={setNbPage} />
                </>
              )}
            </div>
          )}
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default Nearby;
